import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Save, ArrowBack, Preview } from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  GET_TREATMENTS,
  GET_TREATMENT,
  UPDATE_TREATMENT,
  CREATE_TREATMENT,
} from '../graphql/operations';
import { Treatment, TreatmentInput, UpdateTreatmentInput } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`treatment-tabpanel-${index}`}
      aria-labelledby={`treatment-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TreatmentEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState<TreatmentInput>({
    name: '',
    description: '',
    treatment_type: 'OTC',
    instructions: '',
    regular_price: undefined,
    sale_price: undefined,
    image_url: '',
    is_active: true,
  });
  const [markdownPreview, setMarkdownPreview] = useState(false);

  const { data, loading, error } = useQuery(GET_TREATMENT, {
    variables: { id },
    skip: isNew,
    onCompleted: (data) => {
      if (data.getTreatment) {
        const treatment = data.getTreatment;
        setFormData({
          name: treatment.name || '',
          description: treatment.description || '',
          treatment_type: treatment.treatment_type || 'OTC',
          instructions: treatment.instructions || '',
          regular_price: treatment.regular_price,
          sale_price: treatment.sale_price,
          image_url: treatment.image_url || '',
          is_active: treatment.is_active ?? true,
        });
      }
    },
  });

  const [createTreatment, { loading: creating }] = useMutation(CREATE_TREATMENT, {
    update: (cache, { data }) => {
      if (!data) return;
      
      const createTreatmentData = data.createTreatment;
      const existingTreatmentsQuery = cache.readQuery<{ listTreatments: { items: Treatment[], nextToken?: string } }>({ 
        query: GET_TREATMENTS 
      });

      if (existingTreatmentsQuery?.listTreatments) {
        cache.writeQuery({
          query: GET_TREATMENTS,
          data: {
            listTreatments: {
              ...existingTreatmentsQuery.listTreatments,
              items: [createTreatmentData, ...existingTreatmentsQuery.listTreatments.items],
            },
          },
        });
      }
    },
    onCompleted: (data) => {
      navigate(`/treatments/${data.createTreatment.id}`);
    },
    onError: (error) => {
      console.error('Error creating treatment:', error);
    },
  });

  const [updateTreatment, { loading: updating }] = useMutation(UPDATE_TREATMENT, {
    update: (cache, { data }, { variables }) => {
      const existingTreatmentsQuery = cache.readQuery<{ listTreatments: { items: Treatment[], nextToken?: string } }>({ 
        query: GET_TREATMENTS 
      });

      if (existingTreatmentsQuery?.listTreatments && variables) {
        const existingTreatments = existingTreatmentsQuery.listTreatments.items;
        const newTreatments = existingTreatments.map((treatment: Treatment) => {
          if (treatment.id === variables.id) {
            return { ...treatment, ...variables.input };
          }
          return treatment;
        });

        cache.writeQuery({
          query: GET_TREATMENTS,
          data: {
            listTreatments: {
              ...existingTreatmentsQuery.listTreatments,
              items: newTreatments,
            },
          },
        });
      }
    },
    onError: (error) => {
      console.error('Error updating treatment:', error);
    },
  });

  const handleInputChange = (field: keyof TreatmentInput) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'checkbox'
      ? (event.target as HTMLInputElement).checked
      : event.target.value;
    
    setFormData((prev) => ({
      ...prev,
      [field]: (field === 'regular_price' || field === 'sale_price') && typeof value === 'string'
        ? (value === '' ? undefined : parseFloat(value))
        : value,
    }));
  };

  const handleSave = async () => {
    try {
      if (isNew) {
        await createTreatment({ variables: { input: formData } });
      } else {
        const updateInput: UpdateTreatmentInput = { ...formData };
        await updateTreatment({ variables: { id, input: updateInput } });
      }
    } catch (error) {
      console.error('Error saving treatment:', error);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const treatment = data?.getTreatment;

  if (!isNew && loading) return <Typography>Loading treatment...</Typography>;
  if (!isNew && error) return <Alert severity="error">Error loading treatment: {error.message}</Alert>;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackClick}
            variant="outlined"
          >
            Back to Treatments
          </Button>
          <Typography variant="h4">
            {isNew ? 'Create New Treatment' : `Edit: ${treatment?.name}`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={creating || updating || !formData.name.trim()}
        >
          {creating || updating ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="treatment edit tabs"
        >
          <Tab label="Basic Info" />
          <Tab label="Description" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Treatment Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  required
                  helperText="Enter the full name of the treatment"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Treatment Type</InputLabel>
                  <Select
                    value={formData.treatment_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, treatment_type: e.target.value as any }))}
                  >
                    <MenuItem value="PRESCRIPTION">Prescription</MenuItem>
                    <MenuItem value="OTC">Over the Counter</MenuItem>
                    <MenuItem value="HOME_REMEDY">Home Remedy</MenuItem>
                    <MenuItem value="LIFESTYLE">Lifestyle</MenuItem>
                    <MenuItem value="PROCEDURE">Procedure</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Regular Price"
                  value={formData.regular_price || ''}
                  onChange={handleInputChange('regular_price')}
                  inputProps={{ step: 0.01, min: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Sale Price"
                  value={formData.sale_price || ''}
                  onChange={handleInputChange('sale_price')}
                  inputProps={{ step: 0.01, min: 0 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Image URL"
                  value={formData.image_url}
                  onChange={handleInputChange('image_url')}
                  placeholder="https://example.com/image.jpg"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Description (Markdown)</Typography>
              <Button
                startIcon={<Preview />}
                onClick={() => setMarkdownPreview(!markdownPreview)}
                variant="outlined"
                size="small"
              >
                {markdownPreview ? 'Edit' : 'Preview'}
              </Button>
            </Box>
            
            {markdownPreview ? (
              <Paper variant="outlined" sx={{ p: 2, minHeight: 400, maxHeight: 600, overflow: 'auto' }}>
                <ReactMarkdown>{formData.description || 'No description yet...'}</ReactMarkdown>
              </Paper>
            ) : (
              <TextField
                fullWidth
                multiline
                rows={20}
                value={formData.description}
                onChange={handleInputChange('description')}
                placeholder="Write detailed description about the treatment in Markdown format...

Example:
# Treatment Overview
This medication is used to...

## Usage Instructions
- Take once daily with food
- Avoid alcohol while using

## Side Effects
Side effects may include...

## Precautions
Do not use if..."
                variant="outlined"
              />
            )}
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
};

export default TreatmentEdit;
